import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowDownRight, ArrowUpRight, ArrowLeftRight, Loader2, Receipt, Sparkles, Wallet,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import { EmptyState, StatCard, TypeBadge, tdClass, thClass } from "../components/Common";
import QuickInput from "../components/QuickInput";

const PIE_COLORS = ["#F43F5E", "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

const BOOK_META = {
  resmi: { name: "Kas Resmi", saldoLabel: "Saldo Kas Resmi", testid: "resmi", color: "blue" },
  imf: { name: "Kas IMF", saldoLabel: "Saldo IMF", testid: "imf", color: "emerald" },
};

export default function Dashboard() {
  const { hasPerm } = useAuth();
  const canResmi = hasPerm("view_saldo_resmi");
  const canImf = hasPerm("view_saldo_imf");
  const [book, setBook] = useState(null);
  const [books, setBooks] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadBooks = useCallback(() => {
    api.get("/stats/books").then((r) => setBooks(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!book) return;
    setLoading(true);
    api.get(`/stats/dashboard?book=${book}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [book]);

  useEffect(() => { loadBooks(); }, [loadBooks]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!book) {
      if (canResmi && !canImf) setBook("resmi");
      else if (canImf && !canResmi) setBook("imf");
    }
  }, [book, canResmi, canImf]);

  const seedSample = async () => {
    setSeeding(true);
    try {
      const { data: res } = await api.post("/seed/sample");
      toast.success(res.message);
      load();
      loadBooks();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };

  const refreshAll = () => { load(); loadBooks(); };
  const meta = book ? BOOK_META[book] : null;

  const bookCardCls = (active) =>
    `text-left rounded-xl border-2 p-5 transition-all ${
      active
        ? "border-slate-800 bg-slate-900 text-white shadow-md"
        : "border-slate-800 bg-[#1E293B] hover:border-slate-700 hover:shadow-sm"
    }`;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {hasPerm("create_income", "create_expense", "create_income_imf", "create_expense_imf") && (
        <div className="fade-up" style={{ animationDelay: "0.1s" }}>
          <QuickInput onSaved={refreshAll} />
        </div>
      )}

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4" data-testid="book-selector">
        <div className="flex items-center gap-2 mb-3">
          <ArrowLeftRight className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Pilih kas untuk menampilkan laporan
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl fade-up" style={{ animationDelay: "0.15s" }}>
          {canResmi && (
            <button data-testid="book-card-resmi" onClick={() => setBook("resmi")} className={bookCardCls(book === "resmi")}>
              <p className={`text-[10px] font-bold tracking-widest uppercase ${book === "resmi" ? "text-slate-400" : "text-slate-400"}`}>Kas Resmi</p>
              <p className={`font-num text-2xl font-bold mt-1.5 ${book === "resmi" ? "text-white" : "text-slate-100"}`}>
                {books ? formatRupiah(books.resmi) : "…"}
              </p>
              <p className={`text-xs mt-1 ${book === "resmi" ? "text-slate-400" : "text-slate-400"}`}>
                {books ? `${books.resmi_count} transaksi` : ""}
              </p>
            </button>
          )}
          {canImf && (
            <button data-testid="book-card-imf" onClick={() => setBook("imf")} className={bookCardCls(book === "imf")}>
              <p className={`text-[10px] font-bold tracking-widest uppercase ${book === "imf" ? "text-slate-400" : "text-slate-400"}`}>Kas IMF</p>
              <p className={`font-num text-2xl font-bold mt-1.5 ${book === "imf" ? "text-emerald-400" : "text-slate-100"}`}>
                {books ? formatRupiah(books.imf) : "…"}
              </p>
              <p className={`text-xs mt-1 ${book === "imf" ? "text-slate-400" : "text-slate-400"}`}>
                {books ? `${books.imf_count} transaksi` : ""}
              </p>
            </button>
          )}
        </div>
      </div>

      {!book && (
        <EmptyState
          title="Pilih kas terlebih dahulu"
          description="Klik salah satu kartu kas di atas untuk menampilkan saldo, grafik, dan laporan kas tersebut."
        />
      )}

      {book && loading && (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      )}

      {book && !loading && data && data.count === 0 && (
        <div className="space-y-6">
          <div className="max-w-md">
            <StatCard testid="kpi-card-saldo" title={meta.saldoLabel} value={formatRupiah(data.balance)} numeric={data.balance} icon={Wallet} accent={meta.color} />
          </div>
          <EmptyState
          title={`Belum ada transaksi di ${meta.name}`}
          description="Gunakan Quick Input di atas untuk mencatat transaksi pertama pada kas ini."
          action={null}
        />
        </div>
      )}

      {book && !loading && data && data.count > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 fade-up" style={{ animationDelay: "0.2s" }}>
            <StatCard testid="kpi-card-saldo" title={meta.saldoLabel} value={formatRupiah(data.balance)} numeric={data.balance} icon={Wallet} accent={meta.color} />
            <StatCard testid="kpi-card-pemasukan" title="Total Pemasukan" value={formatRupiah(data.total_income)} numeric={data.total_income} icon={ArrowDownRight} accent="emerald" />
            <StatCard testid="kpi-card-pengeluaran" title="Total Pengeluaran" value={formatRupiah(data.total_expense)} numeric={data.total_expense} icon={ArrowUpRight} accent="rose" />
            <StatCard testid="kpi-card-transaksi" title="Jumlah Transaksi" value={data.count} numeric={data.count} numFormat={(n) => n} icon={Receipt} accent="slate" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
            <div className="xl:col-span-2 bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
              <h3 className="text-base font-bold text-slate-100">Pemasukan vs Pengeluaran per Bulan</h3>
              <p className="text-xs text-slate-400 mb-4">{meta.name}</p>
              <div data-testid="chart-income-vs-expense" className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => formatRupiah(v)} />
                    <Legend />
                    <Bar dataKey="income" name="Pemasukan" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" name="Pengeluaran" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
              <h3 className="text-base font-bold text-slate-100">Komposisi Pengeluaran</h3>
              <p className="text-xs text-slate-400 mb-4">Berdasarkan kategori</p>
              <div data-testid="chart-kategori-distribution" className="h-72">
                {data.expense_by_category.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center pt-20">Belum ada pengeluaran</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.expense_by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {data.expense_by_category.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatRupiah(v)} />
                      <Legend layout="horizontal" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
            <h3 className="text-base font-bold text-slate-100">Perkembangan Saldo</h3>
            <p className="text-xs text-slate-400 mb-4">{meta.name} — saldo akhir setiap bulan</p>
            <div data-testid="chart-saldo-trend" className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="saldoGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                  <Tooltip formatter={(v) => formatRupiah(v)} />
                  <Area type="monotone" dataKey="balance" name="Saldo" stroke="#38BDF8" strokeWidth={2.5} fill="url(#saldoGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 fade-up" style={{ animationDelay: "0.4s" }}>
            <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-100">Transaksi Terbaru</h3>
                  <p className="text-xs text-slate-400">5 transaksi terakhir — {meta.name}</p>
                </div>
                {book === "resmi" && (
                  <Link to="/transaksi" data-testid="link-all-transactions" className="text-xs font-semibold text-cyan-400 hover:underline">
                    Lihat Semua
                  </Link>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm text-left">
                  <thead>
                    <tr>
                      <th className={thClass}>Tanggal</th>
                      <th className={thClass}>Uraian</th>
                      <th className={thClass}>Jenis</th>
                      <th className={`${thClass} text-right`}>Nominal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-800/40">
                        <td className={`${tdClass} whitespace-nowrap`}>{formatDateID(t.date)}</td>
                        <td className={tdClass}>
                          <p className="font-medium text-slate-100 line-clamp-1">{t.description}</p>
                          <p className="text-xs text-slate-400">{t.transaction_number}</p>
                        </td>
                        <td className={tdClass}><TypeBadge type={t.type} /></td>
                        <td className={`${tdClass} text-right font-num font-semibold ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                          {t.type === "income" ? "+" : "−"}{formatRupiah(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-base font-bold text-slate-100">Ringkasan Bulanan</h3>
                <p className="text-xs text-slate-400">Pemasukan, pengeluaran, dan saldo akhir per bulan</p>
              </div>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full border-collapse text-sm text-left">
                  <thead className="sticky top-0">
                    <tr>
                      <th className={thClass}>Bulan</th>
                      <th className={`${thClass} text-right`}>Pemasukan</th>
                      <th className={`${thClass} text-right`}>Pengeluaran</th>
                      <th className={`${thClass} text-right`}>Saldo Akhir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.monthly].reverse().map((m) => (
                      <tr key={m.key} className="hover:bg-slate-800/40">
                        <td className={`${tdClass} font-medium text-slate-100`}>{m.month_name}</td>
                        <td className={`${tdClass} text-right font-num text-emerald-400`}>{formatRupiah(m.income)}</td>
                        <td className={`${tdClass} text-right font-num text-rose-400`}>{formatRupiah(m.expense)}</td>
                        <td className={`${tdClass} text-right font-num font-semibold text-blue-300`}>{formatRupiah(m.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
